import { useState, useTransition } from "react";
import { toast } from "sonner";
import { Icone } from "@/components/Icone";
import { Botao } from "@/components/casca/formulario";
import { CATEGORIAS, preencher, type FonteVariavel, type ModeloMensagem } from "@/lib/modelos";
import { atualizarModeloAction, criarModeloAction, removerModeloAction } from "@/web/src/acoes-config";

const ENTRADA =
  "w-full rounded-[10px] border border-[var(--color-borda)] bg-[var(--color-painel)] px-3 py-2 text-sm text-[var(--color-tinta)] outline-none transition-colors placeholder:text-[var(--color-fraco)] focus:border-[var(--color-marca)] focus:bg-[var(--color-cartao)]";

const FONTES: Array<{ id: FonteVariavel; rotulo: string }> = [
  { id: "nome", rotulo: "Nome do cliente (ficha)" },
  { id: "interesse", rotulo: "Interesse / serviço (ficha)" },
  { id: "manual", rotulo: "Digitado na hora" },
];

export function EditorModelos({ modelos, personalizados, custoUSD }: { modelos: ModeloMensagem[]; personalizados: boolean; custoUSD: number }) {
  const [editando, setEditando] = useState<{ atual: ModeloMensagem | null } | null>(null);
  const [pendente, iniciar] = useTransition();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-[13.5px] leading-relaxed text-[var(--color-suave)]">
          {personalizados ? "Modelos desta clínica." : "Estes são os modelos padrão. Ao editar um, todos passam a ser seus."}
          {custoUSD > 0 ? ` Cada envio custa US$ ${custoUSD.toFixed(4)}.` : ""}
        </p>
        <Botao icone="mais" onClick={() => setEditando({ atual: null })}>
          Novo modelo
        </Botao>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {modelos.map((m) => (
          <article key={m.nome} className="flex flex-col rounded-[14px] bg-[var(--color-cartao)] p-4 shadow-cartao">
            <div className="mb-2 flex items-center gap-2">
              <code className="flex-1 truncate font-mono text-xs text-base font-bold text-[var(--color-tinta)]">{m.nome}</code>
              <span className="rounded-full bg-[var(--color-painel)] px-2.5 py-0.5 text-xs font-semibold text-[var(--color-suave)] shadow-[inset_0_0_0_1px_var(--color-borda)]">
                {CATEGORIAS.find((c) => c.id === m.categoria)?.rotulo ?? m.categoria}
              </span>
            </div>
            <p className="flex-1 rounded-xl bg-[var(--color-painel)] px-3 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-wrap text-[var(--color-suave)]">
              {preencher(m.corpo, m.variaveis.map((v) => `[${v.rotulo}]`))}
            </p>
            <div className="mt-2 flex justify-end gap-1">
              <button type="button" aria-label="Editar" onClick={() => setEditando({ atual: m })} className="flex h-9 w-9 items-center justify-center rounded-full text-[var(--color-fraco)] transition-colors hover:bg-[var(--color-painel)] hover:text-[var(--color-tinta)]">
                <Icone nome="editar" className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                aria-label="Remover"
                disabled={pendente}
                onClick={() => {
                  if (!confirm(`Remover o modelo "${m.nome}"?`)) return;
                  iniciar(async () => {
                    const r = await removerModeloAction(m.nome);
                    if (!r.ok) toast.error(r.erro);
                    else toast.success("Modelo removido.");
                  });
                }}
                className="flex h-9 w-9 items-center justify-center rounded-full text-[var(--color-fraco)] transition-colors hover:bg-[var(--color-alerta-fundo)] hover:text-[var(--color-alerta)]"
              >
                <Icone nome="lixeira" className="h-3.5 w-3.5" />
              </button>
            </div>
          </article>
        ))}
      </div>

      {editando && <DialogoModelo atual={editando.atual} aoFechar={() => setEditando(null)} />}
    </div>
  );
}

function DialogoModelo({ atual, aoFechar }: { atual: ModeloMensagem | null; aoFechar: () => void }) {
  const [m, setM] = useState<ModeloMensagem>(atual ?? { nome: "", categoria: "UTILIDADE", corpo: "", variaveis: [] });
  const [pendente, iniciar] = useTransition();

  // As variáveis acompanham o texto: {{3}} no corpo pede três declaradas.
  const usadas = [...m.corpo.matchAll(/\{\{(\d+)\}\}/g)].map((x) => Number(x[1]));
  const maior = Math.max(0, ...usadas);
  const variaveis = Array.from({ length: Math.max(maior, m.variaveis.length) }, (_, i) => m.variaveis[i] ?? { rotulo: `Variável ${i + 1}`, fonte: "manual" as FonteVariavel });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4" onClick={aoFechar}>
      <div className="anim-scale max-h-[86vh] w-full max-w-lg overflow-y-auto rounded-[18px] bg-[var(--color-elevado)] p-6 text-[var(--color-tinta)] shadow-flutua" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold">{atual ? "Editar modelo" : "Novo modelo"}</h2>
        <p className="mb-4 text-[13.5px] leading-relaxed text-[var(--color-suave)]">
          Na API oficial o nome precisa bater com um modelo aprovado no Gerenciador da Meta — isto aqui não
          fala com a Meta.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-semibold text-[var(--color-suave)]">Nome</span>
            <input value={m.nome} onChange={(e) => setM({ ...m, nome: e.target.value })} placeholder="lembrete_retorno" className={`${ENTRADA} font-mono`} />
            <span className="mt-1 block text-xs leading-relaxed text-[var(--color-fraco)]">letras minúsculas, números e _</span>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-semibold text-[var(--color-suave)]">Categoria</span>
            <select value={m.categoria} onChange={(e) => setM({ ...m, categoria: e.target.value as ModeloMensagem["categoria"] })} className={ENTRADA}>
              {CATEGORIAS.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.rotulo} — {c.descricao}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="mt-3 block">
          <span className="mb-1.5 block text-[13px] font-semibold text-[var(--color-suave)]">Texto</span>
          <textarea rows={4} value={m.corpo} onChange={(e) => setM({ ...m, corpo: e.target.value })} placeholder="Oi {{1}}! Passando para lembrar da sua consulta no dia {{2}}. Até lá!" className={ENTRADA} />
          <span className="mt-1 block text-xs leading-relaxed text-[var(--color-fraco)]">
            Use {"{{1}}"}, {"{{2}}"}… para o que muda por pessoa.
          </span>
        </label>

        {variaveis.slice(0, maior).map((v, i) => (
          <div key={i} className="mt-2 grid gap-2 sm:grid-cols-[70px_1fr_1fr]">
            <span className="self-center font-mono text-xs text-[var(--color-suave)]">{`{{${i + 1}}}`}</span>
            <input value={v.rotulo} onChange={(e) => setM({ ...m, variaveis: variaveis.map((x, j) => (j === i ? { ...x, rotulo: e.target.value } : x)) })} placeholder="Rótulo" className={ENTRADA} />
            <select value={v.fonte} onChange={(e) => setM({ ...m, variaveis: variaveis.map((x, j) => (j === i ? { ...x, fonte: e.target.value as FonteVariavel } : x)) })} className={ENTRADA}>
              {FONTES.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.rotulo}
                </option>
              ))}
            </select>
          </div>
        ))}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={aoFechar} className="h-9 rounded-full px-4 text-[13px] font-semibold text-[var(--color-suave)] transition-colors hover:bg-[var(--color-painel)] hover:text-[var(--color-tinta)]">
            Cancelar
          </button>
          <button
            type="button"
            disabled={pendente}
            onClick={() =>
              iniciar(async () => {
                const final = { ...m, nome: m.nome.trim(), variaveis: variaveis.slice(0, maior) };
                const r = atual ? await atualizarModeloAction(atual.nome, final) : await criarModeloAction(final);
                if (!r.ok) {
                  toast.error(r.erro);
                  return;
                }
                toast.success("Modelo salvo.");
                aoFechar();
              })
            }
            className="h-9 rounded-full bg-[var(--color-marca)] px-4 text-[13px] font-semibold text-[var(--color-sobre-marca)] transition-[filter] hover:brightness-110 disabled:opacity-40"
          >
            {pendente ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}
